import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { SearchTermSet, SearchTermStatus } from "@/types/search-term";
import type {
  RegenerateSearchTermSlotsRequest,
  RegenerateSearchTermSlotsResponse,
} from "@/types/search-term";
import { DEFAULT_STALE_TIME } from "@/constants";
import type { AiValidationResult } from "@/types/ai-validation";

type SearchTermSnapshot = {
  queryKey: readonly unknown[];
  data: SearchTermSet[] | undefined;
}[];

const getQueryKey = (productId: number) =>
  ["search-term-sets", "list", productId] as const;

const takeSnapshot = (productId: number): SearchTermSnapshot => {
  const queryKey = getQueryKey(productId);
  const data = queryClient.getQueryData<SearchTermSet[]>(queryKey);
  return [{ queryKey, data }];
};

const restoreSnapshot = (snapshot: SearchTermSnapshot) => {
  for (const { queryKey, data } of snapshot) {
    queryClient.setQueryData(queryKey, data);
  }
};

/**
 * The poll predicate for `useGetSearchTermSets`: poll every 3 s while any set
 * in the list is generating, refreshing or has a regeneration claim in flight
 * (`regeneration_slots != null`); stop when nothing is in flight
 * (`decisions/search-term-progress-is-polled-not-broadcast.md`). A separate
 * exported function, the way `keyword-bank-run.api.ts`'s
 * `computeKeywordBankRunRefetchInterval` is, so the predicate is directly
 * unit-testable without inspecting the registered `refetchInterval` callback.
 */
export function computeSearchTermSetsRefetchInterval(
  data: SearchTermSet[] | undefined,
): number | false {
  if (
    data?.some(
      (s) =>
        s.status === SearchTermStatus.Pending ||
        s.status === SearchTermStatus.Generating ||
        s.status === SearchTermStatus.Refreshing ||
        s.regeneration_slots != null,
    )
  ) {
    return 3000;
  }
  return false;
}

export const useGetSearchTermSets = (productId: number | undefined) =>
  useQuery<SearchTermSet[]>({
    queryKey: ["search-term-sets", "list", productId],
    queryFn: () =>
      apiService.get<SearchTermSet[], unknown>("/search-term-sets", {
        product_id: productId,
      }),
    enabled: !!productId,
    staleTime: DEFAULT_STALE_TIME,
    refetchInterval: (query) => computeSearchTermSetsRefetchInterval(query.state.data),
  });

export const useDeleteSearchTermSet = () =>
  useMutation<
    unknown,
    unknown,
    { id: number; productId: number },
    { snapshot: SearchTermSnapshot }
  >({
    mutationFn: ({ id }) =>
      apiService.delete(`/search-term-sets/${id}`),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.productId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.productId);

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) =>
        prev ? prev.filter((s) => s.id !== variables.id) : prev,
      );

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(_error);
    },
  });

export const useUpdateSearchTermSet = () =>
  useMutation<
    unknown,
    unknown,
    { id: number; productId: number; entries: { id: number; content: string }[] },
    { snapshot: SearchTermSnapshot }
  >({
    mutationFn: ({ id, entries }) =>
      apiService.patch(`/search-term-sets/${id}`, { entries }),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.productId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.productId);

      const entryMap = new Map(
        variables.entries?.map((e) => [e.id, e.content]),
      );

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) => {
        if (!prev) return prev;
        return prev.map((set) => {
          if (set.id !== variables.id) return set;
          return {
            ...set,
            entries: set.entries?.map((entry) => {
              const newContent = entryMap.get(entry.id);
              if (newContent === undefined) return entry;
              return { ...entry, content: newContent };
            }),
          };
        });
      });

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(_error);
    },
  });

export type AiReviewFlaggedItem = {
  keyword: string;
  reason: string;
  severity: "high" | "medium" | "low";
};

export const useAiReviewSearchTerms = () =>
  useMutation<
    { flagged: AiReviewFlaggedItem[]; validation?: AiValidationResult },
    unknown,
    { words: string[]; product_name: string; product_id: number }
  >({
    mutationFn: (data) =>
      apiService.post("/search-term-sets/ai-review", data),
    onError: (error) => handleApiError(error),
  });

export const useGenerateSearchTerms = () =>
  useMutation<
    { id: number; status: string },
    unknown,
    { product_id: number; type: "single" | "grouped" }
  >({
    mutationFn: (data) =>
      apiService.post("/search-term-sets/generate", data),
    onSuccess: (data, variables) => {
      const queryKey = getQueryKey(variables.product_id);

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) => {
        const pendingSet: SearchTermSet = {
          id: data.id,
          product_id: variables.product_id,
          type: variables.type,
          status: SearchTermStatus.Pending,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          entries: [],
          origins: [],
        };
        return prev ? [pendingSet, ...prev] : [pendingSet];
      });
    },
    onError: (error) => handleApiError(error),
  });

export const useRefreshSearchTermSet = () =>
  useMutation<
    { id: number; status: string },
    unknown,
    { id: number; productId: number },
    { snapshot: SearchTermSnapshot }
  >({
    mutationFn: ({ id }) =>
      apiService.post(`/search-term-sets/${id}/refresh`),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.productId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.productId);

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) => {
        if (!prev) return prev;
        return prev.map((set) =>
          set.id === variables.id ? { ...set, status: SearchTermStatus.Refreshing } : set,
        );
      });

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(_error);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: getQueryKey(variables.productId) });
    },
  });

export const useRetrySearchTermSet = () =>
  useMutation<
    { id: number; status: string },
    unknown,
    { id: number; productId: number; type: "single" | "grouped" },
    { snapshot: SearchTermSnapshot }
  >({
    mutationFn: async ({ id, productId, type }) => {
      await apiService.delete(`/search-term-sets/${id}`);
      return apiService.post("/search-term-sets/generate", {
        product_id: productId,
        type,
      });
    },
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.productId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.productId);

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) =>
        prev ? prev.filter((s) => s.id !== variables.id) : prev,
      );

      return { snapshot };
    },
    onSuccess: (data, variables) => {
      const queryKey = getQueryKey(variables.productId);

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) => {
        const pendingSet: SearchTermSet = {
          id: data.id,
          product_id: variables.productId,
          type: variables.type,
          status: "pending",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          entries: [],
          origins: [],
        };
        return prev ? [pendingSet, ...prev] : [pendingSet];
      });
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(_error);
    },
  });

/**
 * `POST /api/search-term-sets/{id}/regenerate-slots`
 * (`components/search-term.md § The seventh endpoint`). Errors -- including a
 * stale client's `409`/`422` -- go through the module's existing
 * `handleApiError`, so this hook adds no new copy.
 *
 * On success, merges the `202`'s three keys onto the matching set in the
 * list cache the same way `useGenerateSearchTerms`'s `onSuccess` writes its
 * pending set, rather than only invalidating: the response is what the SPA's
 * FR-8 toast-count snapshot is seeded from, and merging it in immediately is
 * also what lets `computeSearchTermSetsRefetchInterval` see
 * `regeneration_slots != null` and keep polling without waiting on a
 * round-trip. `regeneration_failed_at` is reset to `null` here because the
 * claim itself clears it server-side
 * (`decisions/a-slot-regeneration-is-claimed-on-the-set-row.md`).
 */
export const useRegenerateSearchTermSlots = () =>
  useMutation<
    RegenerateSearchTermSlotsResponse,
    unknown,
    { id: number; productId: number; slot_indices: number[] }
  >({
    mutationFn: ({ id, slot_indices }) =>
      apiService.post<RegenerateSearchTermSlotsResponse, RegenerateSearchTermSlotsRequest>(
        `/search-term-sets/${id}/regenerate-slots`,
        { slot_indices },
      ),
    onSuccess: (data, variables) => {
      const queryKey = getQueryKey(variables.productId);

      queryClient.setQueryData<SearchTermSet[]>(queryKey, (prev) => {
        if (!prev) return prev;
        return prev.map((set) =>
          set.id === data.id
            ? {
                ...set,
                regeneration_slots: data.regeneration_slots,
                regeneration_started_at: data.regeneration_started_at,
                regeneration_failed_at: null,
              }
            : set,
        );
      });
    },
    onError: (error) => handleApiError(error),
  });
